import { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu } from 'lucide-react'
import { Sidebar } from './Sidebar.js'
import { SearchModal } from '../features/search/SearchModal.js'
import { HelpModal } from '../features/help/HelpModal.js'
import { isTypingTarget } from '../lib/keyboard.js'

/** Milisegundos para completar el atajo compuesto `g` + `m` (CLAUDE.md §6). */
const CHORD_WINDOW_MS = 600

export function AppLayout() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchOpen, setSearchOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  /** En `ref`: no necesita causar un render, solo se lee dentro del propio listener. */
  const gArmedRef = useRef(false)

  /**
   * La navegación (incluida la que dispara el propio cajón al elegir un
   * enlace) cierra el cajón: sin esto se quedaría abierto tapando la pantalla
   * nueva en móvil. Ajuste de estado durante el render en vez de un efecto:
   * es el patrón que React recomienda para "reiniciar estado cuando cambia
   * una prop", y evita la cascada de renders de un `setState` en un efecto.
   */
  const [lastPathname, setLastPathname] = useState(location.pathname)
  if (location.pathname !== lastPathname) {
    setLastPathname(location.pathname)
    setDrawerOpen(false)
  }

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (isTypingTarget(event.target)) return

      if (event.key === '/') {
        event.preventDefault()
        setSearchOpen(true)
        return
      }
      if (event.key === '?') {
        setHelpOpen(true)
        return
      }
      if (event.key === 'g') {
        gArmedRef.current = true
        setTimeout(() => {
          gArmedRef.current = false
        }, CHORD_WINDOW_MS)
        return
      }
      if (event.key === 'm' && gArmedRef.current) {
        gArmedRef.current = false
        void navigate('/')
        return
      }
      if (event.key === 'c') {
        // Solo lo escucha el tablero de un proyecto (único sitio con un
        // proyecto al que asignar la tarea nueva); en cualquier otra pantalla
        // no tiene efecto (ver docs/DECISIONS.md, fase 5).
        window.dispatchEvent(new CustomEvent('wb:create-task'))
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [navigate])

  return (
    <div className="flex h-dvh flex-col bg-bg md:flex-row">
      {/* Cabecera solo en móvil (CLAUDE.md §6, fase 6): la barra lateral es un
          cajón por debajo de 768px, así que hace falta algo que lo abra. */}
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-2 md:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label={t('sidebar.openMenu')}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-card text-text hover:bg-surface-hi focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>
        <img src="/logo.png" alt="Witty Games" className="h-7 w-auto" />
      </header>

      <Sidebar open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      {/* Un único scroll, el de la página (post fase 7): tableros y listas
          crecen en vertical lo que necesiten, sin barra por columna, y el pie
          queda al final del contenido, no fijo. */}
      <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
        <div className="flex-1">
          <Outlet />
        </div>
        <footer className="shrink-0 px-6 py-8 text-center text-meta text-text-muted">
          {t('footer.text', { year: new Date().getFullYear() })}
        </footer>
      </main>

      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} />}
      {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} />}
    </div>
  )
}
